/** Static content for the /mcp landing page. */

import { APP_ROUTES } from '@/shared/constants/navigation';

// Last content change. Bump only when the page content changes: it feeds the page's JSON-LD and its sitemap lastmod.
export const MCP_UPDATED = '2026-10-16';

export const MCP_SERVER_URL = 'https://api.binary.so/mcp';

/** AI clients the server is known to work with, in the order they are named in copy. */
export const MCP_CLIENTS = ['Claude', 'ChatGPT', 'Codex', 'Grok', 'Cursor', 'Claude Code'] as const;

/** Prompts a visitor can paste into their assistant, grouped by the job they do. */
export const MCP_CAPABILITY_GROUPS = [
	{
		name: 'Analytics',
		prompts: [
			'How fast are we moving in this role?',
			'How many people did each recruiter source and shortlist?',
			'How many interviews has each interviewer taken last month?',
		],
	},
	{
		name: 'Searching and screening',
		prompts: [
			'Read all profiles and find iOS engineers with 2+ years exp',
			'Has anyone from Amazon ever applied to us?',
			'Screen the whole pipeline against the JD and write the scores back as a column',
		],
	},
	{
		name: 'Data import',
		prompts: [
			'Add all people who have emailed me for a job to the hiring funnel',
			'Import this google form/sheet to binary and help me screen',
		],
	},
	{
		name: 'New openings',
		prompts: [
			'Create a new job for marketing and help me write a JD',
			'Suggest me some AI-proof questions for this hiring form',
			"Let's make this job live on our LinkedIn page too",
		],
	},
	{
		name: 'Interviews',
		prompts: [
			"Check Priya's and Sam's open slots next week and schedule a 45-minute interview",
			'Reschedule the 3pm interview to Thursday and let the candidate know',
		],
	},
];

/** Client-by-client setup steps. */
export const MCP_CLIENT_SETUP: { client: string; steps: string[] }[] = [
	{
		client: 'Claude',
		steps: [
			'Open Settings → Connectors and choose Add custom connector.',
			`Paste ${MCP_SERVER_URL} as the server URL.`,
			'Sign in with your Binary account and pick the workspace to connect.',
		],
	},
	{
		client: 'ChatGPT',
		steps: [
			'Open Settings → Connectors and create a new connector (developer mode must be enabled).',
			`Paste ${MCP_SERVER_URL} as the MCP server URL.`,
			'Sign in with your Binary account when prompted, then enable the connector in a chat.',
		],
	},
	{
		client: 'Cursor',
		steps: [
			'Open Cursor Settings → MCP and add a new server.',
			`Set the URL to ${MCP_SERVER_URL}.`,
			'Complete the Binary sign-in in the browser window that opens.',
		],
	},
	{
		client: 'Claude Code',
		steps: [
			`Run: claude mcp add --transport http binary ${MCP_SERVER_URL}`,
			'Type /mcp in a session and sign in with your Binary account.',
		],
	},
	{
		client: 'Codex',
		steps: [
			`Run: codex mcp add binary --url ${MCP_SERVER_URL}`,
			'Sign in with your Binary account when Codex opens the consent page.',
		],
	},
];

/**
 * How Binary's MCP compares with other ATS MCP servers, from each vendor's
 * pricing or docs pages, checked 24 Oct 2026.
 */
export const MCP_COMPETITOR_ROWS: {
	name: string;
	availability: string;
	access: string;
	href?: string;
}[] = [
	{
		name: 'Binary',
		availability: 'Every plan, including Free; no request cap',
		access: 'Read and write, including scheduling, rescheduling and cancelling interviews',
	},
	{
		name: 'Workable',
		availability: 'All plans at no additional cost (from $299/mo); OAuth per user',
		access: 'Read and write: 39 write tools, from stage moves to offer approvals; no scheduling',
		href: '/compare/binary-vs-workable',
	},
	{
		name: 'Greenhouse',
		availability: 'Open beta on Core, Plus and Pro (custom-priced, annual)',
		access: 'Read or write per endpoint, scoped by a Site Admin',
		href: '/compare/binary-vs-greenhouse',
	},
	{
		name: 'Ashby',
		availability: 'Open beta on all plans (from $400/mo)',
		access: 'Read, plus limited writes: notes, stages, candidates',
		href: '/compare/binary-vs-ashby',
	},
	{
		name: 'Dover',
		availability: 'Free plan with a monthly request allowance; unlimited on Premium ($199/mo)',
		access: 'Read and write, including scheduling, rescheduling and cancelling interviews',
		href: '/compare/binary-vs-dover',
	},
	{
		name: 'Recruit CRM',
		availability: 'Business ($135/user/mo) and Enterprise only',
		access: 'Read and write, including scheduling, rescheduling and cancelling interviews',
		href: '/compare/binary-vs-recruit-crm',
	},
	{
		name: 'Zoho Recruit',
		availability: 'Included at no extra cost since July 2026; free-edition availability not stated',
		access: 'Read, plus the updates you allow',
		href: '/compare/binary-vs-zoho-recruit',
	},
	{
		name: 'Gem',
		availability: 'Gem customers; official Claude and ChatGPT connectors since August 2026',
		access: 'Read and reports; no write actions documented',
		href: '/compare/binary-vs-gem',
	},
	{
		name: 'Teamtailor',
		availability: 'Quote-based plan; enabled by a Company Admin',
		access: 'Read, create-and-modify, delete by role',
		href: '/compare/binary-vs-teamtailor',
	},
	{
		name: 'Breezy HR',
		availability: 'Beta; Breezy Intelligence add-on on every plan, including free Bootstrap',
		access: 'Read and write: stages, notes, positions, messages',
		href: '/compare/binary-vs-breezy',
	},
	{
		name: 'BambooHR',
		availability: 'Beta; any account once an admin enables the AI Connectors app',
		access: 'Read openings, applications and statuses; write comments only',
		href: '/compare/binary-vs-bamboohr',
	},
];

export const MCP_FAQ_DATA = [
	{
		question: 'What is the Binary MCP server?',
		answer:
			'MCP stands for Model Context Protocol, an open standard for connecting AI assistants to tools and data. The Binary MCP server lets Claude, ChatGPT and other assistants read your hiring data from the ATS and take actions on your behalf: screen candidates, import data, create jobs and manage interviews. It exposes the same capabilities as the AI agent built into Binary, the AI-native ATS, so you can use whichever assistant you already work in.',
		link: { href: APP_ROUTES.AI_ATS, match: 'AI-native ATS' },
	},
	{
		question: 'Which AI assistants can I use?',
		answer: `Any assistant that supports MCP, including ${MCP_CLIENTS.join(', ')}. The protocol is an open standard, so support keeps widening without us shipping a separate integration for each one.`,
	},
	{
		question: 'What actions can I take via Binary MCP?',
		answer:
			'You can generate reports, search your candidate database, screen candidates in batches and write the scores back, import data from email, sheets or existing forms, create new jobs and post them to job boards, and manage interviews: check interviewer slots, schedule, send booking links, reschedule or cancel. Actions are read and write, not read-only.',
	},
	{
		question: 'Do I need a Binary account?',
		answer:
			'Yes. You sign in with your Binary account when you connect, and choose which workspace the assistant can access. The free plan is enough to start.',
		link: { href: APP_ROUTES.FREE_ATS, match: 'free plan' },
	},
	{
		question: 'Is it secure? What can the agent see?',
		answer:
			'The connection uses OAuth: you sign in on a Binary consent page, pick a workspace, and can revoke access at any time from Settings → Integrations. Every call runs as you, so the assistant inherits exactly your permissions in that workspace, nothing more. Binary does not train models on your data. Whether the AI provider retains your conversation depends on your Claude, ChatGPT or other provider plan, so check their data policy for the account you connect.',
	},
	{
		question: 'Are there any extra charges for using this?',
		answer:
			'No. MCP is included on every plan, including Free, with no per-request fee and no cap on requests. Actions that consume AI credits, such as AI screening, draw from your monthly credits like they would in the dashboard, and paid plans have higher rate limits.',
	},
	{
		question: 'How does Binary MCP compare with Greenhouse MCP or Ashby MCP?',
		answer:
			'Greenhouse MCP is in open beta on its custom-priced tiers with scopes set by a Site Admin; Ashby MCP is in open beta on plans from $400 a month with limited writes; Dover meters MCP on its free plan; Recruit CRM gates it to its $135 per user Business tier; Zoho Recruit, Teamtailor and Gem added theirs in 2026. Binary includes MCP on every plan, including Free, with read and write actions and full interview management. See the comparison table above.',
	},
];
